import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Save, Eye, Edit3, ArrowLeft, Layout, Type, Image, 
  Palette, Globe, Settings, Monitor, Smartphone, Tablet,
  Plus, Trash2, Copy, Move, RefreshCw
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

interface PageContent {
  hero: {
    badge: string;
    title: string;
    subtitle: string;
    primaryCTA: string;
    secondaryCTA: string;
  };
  stats: {
    roas: { value: string; label: string };
    clients: { value: string; label: string };
    views: { value: string; label: string };
    support: { value: string; label: string };
  };
  services: {
    title: string;
    subtitle: string;
    performance: { title: string; description: string };
    growth: { title: string; description: string };
    aiVideo: { title: string; description: string };
  };
  process: {
    title: string;
    subtitle: string;
    features: string[];
    testimonial: {
      text: string;
      author: string;
      position: string;
      rating: string;
    };
  };
  contact: {
    title: string;
    subtitle: string;
    phone: string;
    email: string;
    address: string;
    formLabels: {
      name: string;
      email: string;
      message: string;
      send: string;
    };
  };
}

const PageEditor = () => {
  const { t, language } = useLanguage();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [pageContent, setPageContent] = useState<PageContent>({
    hero: {
      badge: '🚀 وكالة تسويق نمو وأداء',
      title: 'ننمو بالبيانات، ونُبدع بالنتائج',
      subtitle: 'شريك نمو قائم على البيانات يقود العلامات التجارية باستخدام تسويق الأداء',
      primaryCTA: 'تحليل مجاني',
      secondaryCTA: 'شاهد أعمالنا'
    },
    stats: {
      roas: { value: '300%', label: 'متوسط زيادة ROAS' },
      clients: { value: '50+', label: 'عميل راضٍ' },
      views: { value: '2M+', label: 'مشاهدة شهرياً' },
      support: { value: '24/7', label: 'دعم متواصل' }
    },
    services: {
      title: 'خدماتنا المتخصصة',
      subtitle: 'حلول تسويقية شاملة لنمو علامتك التجارية',
      performance: {
        title: 'تسويق الأداء',
        description: 'حملات إعلانية مدفوعة بالبيانات عبر جميع المنصات الرقمية'
      },
      growth: {
        title: 'تسويق النمو',
        description: 'استراتيجيات نمو شاملة لتوسيع قاعدة عملائك'
      },
      aiVideo: {
        title: 'إنتاج فيديو بالذكاء الاصطناعي',
        description: 'محتوى فيديو إبداعي باستخدام أحدث تقنيات الذكاء الاصطناعي'
      }
    },
    process: {
      title: 'لماذا تختار ريشة؟',
      subtitle: 'نحن لا نقدم خدمات فقط، بل نبني شراكات نجاح',
      features: [
        'فريق خبراء متخصص',
        'نتائج قابلة للقياس',
        'تقنيات متطورة',
        'دعم على مدار الساعة'
      ],
      testimonial: {
        text: 'ريشة غيرت مسار أعمالنا بالكامل. النتائج تتحدث عن نفسها.',
        author: 'أحمد محمد',
        position: 'مؤسس شركة تقنية',
        rating: '5.0 من العملاء'
      }
    },
    contact: {
      title: 'تواصل معنا',
      subtitle: 'نحن هنا لمساعدتك في تحقيق أهدافك',
      phone: '+966 50 123 4567',
      email: 'info@reesha.agency',
      address: 'الرياض، المملكة العربية السعودية',
      formLabels: {
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        message: 'الرسالة',
        send: 'إرسال'
      }
    }
  });

  // Load saved content from localStorage
  useEffect(() => {
    const savedContent = localStorage.getItem('page-editor-content');
    if (savedContent) {
      setPageContent(JSON.parse(savedContent));
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'PageEditor2024!') {
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password');
    }
  };

  const handleContentUpdate = (section: keyof PageContent, field: string, value: string | string[]) => {
    setPageContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedUpdate = (section: keyof PageContent, subsection: string, field: string, value: string) => {
    setPageContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleArrayUpdate = (section: keyof PageContent, field: string, index: number, value: string) => {
    setPageContent(prev => {
      const newArray = [...prev[section][field]];
      newArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray
        }
      };
    });
  };

  const handleSaveAll = () => {
    localStorage.setItem('page-editor-content', JSON.stringify(pageContent));
    
    // Update the website content
    updateWebsiteContent();
    
    alert('All content has been updated successfully! The page will reload to apply changes.');
  };

  const updateWebsiteContent = () => {
    // Save content to localStorage with specific keys for the main page to read
    localStorage.setItem('website-content', JSON.stringify(pageContent));
    
    // Trigger a page reload to apply changes
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const sections = [
    { id: 'hero', name: 'Hero Section', icon: Layout },
    { id: 'stats', name: 'Statistics', icon: Type },
    { id: 'services', name: 'Services', icon: Settings },
    { id: 'process', name: 'Process & Why Us', icon: Globe },
    { id: 'contact', name: 'Contact', icon: Type }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Advanced Page Editor</CardTitle>
            <p className="text-muted-foreground">Enter password to edit page content</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter editor password"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Login to Editor
              </Button>
            </form>
            <div className="mt-4 p-3 bg-muted/50 rounded text-sm text-muted-foreground">
              <p><strong>Default Password:</strong> PageEditor2024!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <RouterLink to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Website
                </Button>
              </RouterLink>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Advanced Page Editor</h1>
                <p className="text-sm text-muted-foreground">Edit all content, blocks, and sections</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Preview Mode Toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="w-4 h-4" />
                </Button>
                <Button
                  variant={previewMode === 'tablet' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('tablet')}
                >
                  <Tablet className="w-4 h-4" />
                </Button>
                <Button
                  variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('mobile')}
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
              </div>
              
              <Button variant="outline" onClick={() => setIsPreviewOpen(!isPreviewOpen)}>
                <Eye className="w-4 h-4 mr-2" />
                {isPreviewOpen ? 'Hide Preview' : 'Show Preview'}
              </Button>
              
              <Button onClick={handleSaveAll} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save All Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Page Sections</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    variant={activeSection === section.id ? 'default' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => setActiveSection(section.id)}
                  >
                    <section.icon className="w-4 h-4 mr-2" />
                    {section.name}
                  </Button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Editor */}
          <div className="col-span-9">
            {/* Hero Section Editor */}
            {activeSection === 'hero' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="w-5 h-5" />
                    Hero Section Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="hero-badge">Badge Text</Label>
                    <Input
                      id="hero-badge"
                      value={pageContent.hero.badge}
                      onChange={(e) => handleContentUpdate('hero', 'badge', e.target.value)}
                      placeholder="Badge text (e.g., 🚀 وكالة تسويق نمو وأداء)"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hero-title">Main Title</Label>
                    <Textarea
                      id="hero-title"
                      value={pageContent.hero.title}
                      onChange={(e) => handleContentUpdate('hero', 'title', e.target.value)}
                      placeholder="Main hero title"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hero-subtitle">Subtitle</Label>
                    <Textarea
                      id="hero-subtitle"
                      value={pageContent.hero.subtitle}
                      onChange={(e) => handleContentUpdate('hero', 'subtitle', e.target.value)}
                      placeholder="Hero subtitle description"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hero-primary-cta">Primary Button Text</Label>
                      <Input
                        id="hero-primary-cta"
                        value={pageContent.hero.primaryCTA}
                        onChange={(e) => handleContentUpdate('hero', 'primaryCTA', e.target.value)}
                        placeholder="Primary CTA text"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hero-secondary-cta">Secondary Button Text</Label>
                      <Input
                        id="hero-secondary-cta"
                        value={pageContent.hero.secondaryCTA}
                        onChange={(e) => handleContentUpdate('hero', 'secondaryCTA', e.target.value)}
                        placeholder="Secondary CTA text"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Section Editor */}
            {activeSection === 'stats' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="w-5 h-5" />
                    Statistics Section Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    {Object.entries(pageContent.stats).map(([key, stat]) => (
                      <div key={key} className="space-y-3 p-4 border border-border rounded-lg">
                        <h4 className="font-semibold capitalize">{key}</h4>
                        <div>
                          <Label htmlFor={`stat-${key}-value`}>Value</Label>
                          <Input
                            id={`stat-${key}-value`}
                            value={stat.value}
                            onChange={(e) => handleNestedUpdate('stats', key, 'value', e.target.value)}
                            placeholder="Statistic value"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`stat-${key}-label`}>Label</Label>
                          <Input
                            id={`stat-${key}-label`}
                            value={stat.label}
                            onChange={(e) => handleNestedUpdate('stats', key, 'label', e.target.value)}
                            placeholder="Statistic label"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Services Section Editor */}
            {activeSection === 'services' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Services Section Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="services-title">Section Title</Label>
                    <Input
                      id="services-title"
                      value={pageContent.services.title}
                      onChange={(e) => handleContentUpdate('services', 'title', e.target.value)}
                      placeholder="Services section title"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="services-subtitle">Section Subtitle</Label>
                    <Textarea
                      id="services-subtitle"
                      value={pageContent.services.subtitle}
                      onChange={(e) => handleContentUpdate('services', 'subtitle', e.target.value)}
                      placeholder="Services section subtitle"
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-6">
                    {Object.entries(pageContent.services).filter(([key]) => !['title', 'subtitle'].includes(key)).map(([key, service]) => (
                      <div key={key} className="p-4 border border-border rounded-lg">
                        <h4 className="font-semibold mb-3 capitalize">{key.replace(/([A-Z])/g, ' $1')}</h4>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor={`service-${key}-title`}>Service Title</Label>
                            <Input
                              id={`service-${key}-title`}
                              value={service.title}
                              onChange={(e) => handleNestedUpdate('services', key, 'title', e.target.value)}
                              placeholder="Service title"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`service-${key}-description`}>Service Description</Label>
                            <Textarea
                              id={`service-${key}-description`}
                              value={service.description}
                              onChange={(e) => handleNestedUpdate('services', key, 'description', e.target.value)}
                              placeholder="Service description"
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Process Section Editor */}
            {activeSection === 'process' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Process & Why Us Section Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="process-title">Section Title</Label>
                    <Input
                      id="process-title"
                      value={pageContent.process.title}
                      onChange={(e) => handleContentUpdate('process', 'title', e.target.value)}
                      placeholder="Process section title"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="process-subtitle">Section Subtitle</Label>
                    <Textarea
                      id="process-subtitle"
                      value={pageContent.process.subtitle}
                      onChange={(e) => handleContentUpdate('process', 'subtitle', e.target.value)}
                      placeholder="Process section subtitle"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label>Features List</Label>
                    <div className="space-y-2">
                      {pageContent.process.features.map((feature, index) => (
                        <Input
                          key={index}
                          value={feature}
                          onChange={(e) => handleArrayUpdate('process', 'features', index, e.target.value)}
                          placeholder={`Feature ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-semibold mb-3">Testimonial</h4>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="testimonial-text">Testimonial Text</Label>
                        <Textarea
                          id="testimonial-text"
                          value={pageContent.process.testimonial.text}
                          onChange={(e) => handleNestedUpdate('process', 'testimonial', 'text', e.target.value)}
                          placeholder="Testimonial text"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label htmlFor="testimonial-author">Author Name</Label>
                          <Input
                            id="testimonial-author"
                            value={pageContent.process.testimonial.author}
                            onChange={(e) => handleNestedUpdate('process', 'testimonial', 'author', e.target.value)}
                            placeholder="Author name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="testimonial-position">Position</Label>
                          <Input
                            id="testimonial-position"
                            value={pageContent.process.testimonial.position}
                            onChange={(e) => handleNestedUpdate('process', 'testimonial', 'position', e.target.value)}
                            placeholder="Author position"
                          />
                        </div>
                        <div>
                          <Label htmlFor="testimonial-rating">Rating Text</Label>
                          <Input
                            id="testimonial-rating"
                            value={pageContent.process.testimonial.rating}
                            onChange={(e) => handleNestedUpdate('process', 'testimonial', 'rating', e.target.value)}
                            placeholder="Rating text"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Section Editor */}
            {activeSection === 'contact' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="w-5 h-5" />
                    Contact Section Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="contact-title">Section Title</Label>
                    <Input
                      id="contact-title"
                      value={pageContent.contact.title}
                      onChange={(e) => handleContentUpdate('contact', 'title', e.target.value)}
                      placeholder="Contact section title"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact-subtitle">Section Subtitle</Label>
                    <Textarea
                      id="contact-subtitle"
                      value={pageContent.contact.subtitle}
                      onChange={(e) => handleContentUpdate('contact', 'subtitle', e.target.value)}
                      placeholder="Contact section subtitle"
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="contact-phone">Phone Number</Label>
                      <Input
                        id="contact-phone"
                        value={pageContent.contact.phone}
                        onChange={(e) => handleContentUpdate('contact', 'phone', e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">Email Address</Label>
                      <Input
                        id="contact-email"
                        value={pageContent.contact.email}
                        onChange={(e) => handleContentUpdate('contact', 'email', e.target.value)}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-address">Address</Label>
                      <Input
                        id="contact-address"
                        value={pageContent.contact.address}
                        onChange={(e) => handleContentUpdate('contact', 'address', e.target.value)}
                        placeholder="Address"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-semibold mb-3">Form Labels</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(pageContent.contact.formLabels).map(([key, label]) => (
                        <div key={key}>
                          <Label htmlFor={`form-${key}`}>{key.charAt(0).toUpperCase() + key.slice(1)} Label</Label>
                          <Input
                            id={`form-${key}`}
                            value={label}
                            onChange={(e) => handleNestedUpdate('contact', 'formLabels', key, e.target.value)}
                            placeholder={`${key} label`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditor;